// src/admin/components/buildings/BuildingList.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  RiBuilding3Line, // Industrial building icon
  RiAddLine,
  RiCloseLine,
  RiSearchLine,
  RiPencilLine,
  RiDeleteBin6Line,
  RiEyeLine,
  RiMapPinLine,
  RiAlertLine,
  RiRefreshLine,
  RiFileAddLine,
  RiBarChartBoxLine,
  RiHashtag,
  RiLayoutMasonryLine, // Icon for Blocks
  RiBox3Line // Icon for Showrooms
} from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import {
  getAllBuildings,
  deleteBuilding,
  bulkCreateProperties
} from '../../../models/buildingModel';
import { useTheme } from '../../../contexts/ThemeContext';
import { getCurrentUser } from '../../../models/authModel';
import BuildingSelectionModal from './BuildingSelectionModal';
import BulkApartmentCreator from '../apartments/BulkApartmentCreator';

const BuildingList = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  // Keeping original state names for data stability
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkCreator, setShowBulkCreator] = useState(false);
  const [bulkCreatorBuildingId, setBulkCreatorBuildingId] = useState(null);
  const [showBuildingSelection, setShowBuildingSelection] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    buildingId: null,
    buildingName: ''
  });

  // Fetch complexes data
  const fetchBuildings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getAllBuildings();

      if (response && response.buildings) {
        setBuildings(response.buildings);
      } else {
        setBuildings([]);
      }
    } catch (err) {
      console.error('Error fetching complexes:', err);

      const errorMessages = {
        401: 'Authentication failed. Please log in again.',
        403: 'Access denied. Admin role required.',
        500: 'Server error. Please check if the backend is running.'
      };

      let errorMessage = errorMessages[err.response?.status] ||
        (err.message.includes('Network Error') || err.code === 'ERR_NETWORK'
          ? 'Cannot connect to server. Please check your network connection.'
          : 'Failed to load complex registry data');

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuildings();
  }, []);

  // Filter complexes based on search term
  const filteredBuildings = buildings.filter(building => {
    const searchLower = searchTerm.toLowerCase();
    return (
      building.name.toLowerCase().includes(searchLower) ||
      building.address.toLowerCase().includes(searchLower)
    );
  });

  // Operations handlers
  const handleBulkCreate = (buildingId) => {
    setBulkCreatorBuildingId(buildingId);
    setShowBulkCreator(true);
  };

  const handleBulkCreateSave = async (bulkData) => {
    try {
      const apartments = bulkData.apartments || bulkData;

      if (!apartments || !Array.isArray(apartments)) {
        console.error('Invalid bulk data format', bulkData);
        setError(t('invalid_bulk_showroom_data', 'Invalid data format for bulk showroom creation'));
        return;
      }

      await bulkCreateProperties(bulkCreatorBuildingId, apartments);
      setShowBulkCreator(false);
      setBulkCreatorBuildingId(null);
      fetchBuildings();
    } catch (error) {
      console.error('Error in bulk creation:', error);
      setError(error.message || t('failed_create_bulk_showrooms', 'Failed to create bulk showrooms'));
    }
  };

  const handleBulkCreateCancel = () => {
    setShowBulkCreator(false);
    setBulkCreatorBuildingId(null);
  };

  const handleShowBuildingSelection = () => {
    if (buildings.length === 0) {
      alert(t('create_complex_first') || 'Please register a showroom park first');
      return;
    }

    if (buildings.length === 1) {
      handleBulkCreate(buildings[0].id);
    } else {
      setShowBuildingSelection(true);
    }
  };

  const handleBuildingSelect = (buildingId) => {
    setShowBuildingSelection(false);
    handleBulkCreate(buildingId);
  };

  const handleBuildingSelectionCancel = () => {
    setShowBuildingSelection(false);
  };

  const openDeleteDialog = (buildingId, buildingName) => {
    setDeleteDialog({ isOpen: true, buildingId, buildingName });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, buildingId: null, buildingName: '' });
  };

  const handleDeleteBuilding = async () => {
    if (!deleteDialog.buildingId) return;

    try {
      setDeleting(true);
      setActionError(null);
      await deleteBuilding(deleteDialog.buildingId);
      setBuildings(buildings.filter(building => building.id !== deleteDialog.buildingId));
      closeDeleteDialog();
    } catch (err) {
      console.error('Error deleting complex:', err);
      setActionError(err.message || 'Failed to delete complex');
      closeDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  // Calculate occupancy percentage
  const calculateOccupancy = (building) => {
    const totalApartments = building.actual_apartments || building.totalApartments || 0;
    if (totalApartments === 0) return 0;
    return ((building.occupied_apartments || 0) / totalApartments) * 100;
  };

  // Component rendering blocks
  const renderDeleteDialog = () => (
    deleteDialog.isOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className={`${isDarkMode ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-zinc-200'} rounded-xl border shadow-2xl max-w-md w-full p-6`}>
          <div className="flex items-center mb-4 text-red-500">
            <RiAlertLine size={24} className="mr-2" />
            <h3 className="text-lg font-bold">{t('confirm_deletion') || 'Confirm Deletion'}</h3>
          </div>

          <p className={`${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'} mb-6 font-medium`}>
            {t('delete_complex_confirmation', { name: deleteDialog.buildingName }) ||
              `Are you sure you want to delete the complex "${deleteDialog.buildingName}"? This will permanently remove the park from the registry.`}
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={closeDeleteDialog}
              className={`px-4 py-2 font-semibold rounded-lg ${isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300'} transition-colors`}
              disabled={deleting}
            >
              {t('cancel') || 'Cancel'}
            </button>

            <button
              onClick={handleDeleteBuilding}
              className={`px-4 py-2 font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 flex items-center transition-colors ${deleting ? 'opacity-70 cursor-not-allowed' : ''}`}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {t('deleting') || 'Deleting...'}
                </>
              ) : (
                <>
                  <RiDeleteBin6Line className="mr-2" />
                  {t('delete') || 'Delete'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )
  );

  const renderHeader = () => (
    <div className={`pb-6 mb-6 border-b ${isDarkMode ? 'border-zinc-800' : 'border-zinc-100'}`}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        {/* Title */}
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${isDarkMode ? 'bg-zinc-800 text-red-500' : 'bg-red-50 text-red-600'}`}>
            <RiBuilding3Line size={24} />
          </div>
          <div>
            <h2 className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              {t('showroom_parks') || 'Showroom Parks'}
            </h2>
            <div className="h-0.5 w-10 bg-red-500 rounded-full mt-1"></div>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Search */}
          <div className="relative flex-grow md:flex-grow-0 md:w-60">
            <RiSearchLine className={`absolute top-1/2 -translate-y-1/2 start-3 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} size={16} />
            <input
              type="search"
              className={`block w-full py-2.5 ps-9 pe-3 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-red-500/30 ${isDarkMode
                ? 'bg-zinc-900 border-zinc-700 text-white placeholder-zinc-500'
                : 'bg-gray-50 border-gray-200 text-zinc-900 placeholder-zinc-400'
              }`}
              placeholder={t('search_complexes') || 'Search complexes...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Bulk Add */}
          <button
            onClick={handleShowBuildingSelection}
            disabled={buildings.length === 0}
            className={`px-4 py-2.5 text-sm font-semibold rounded-lg border transition-colors disabled:opacity-40 ${
              isDarkMode
                ? 'bg-zinc-800 text-emerald-400 border-zinc-700 hover:bg-zinc-700'
                : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
            }`}
          >
            {t('bulk_add_showrooms') || 'Bulk Add Showrooms'}
          </button>

          {/* Add Complex */}
          <Link to="/admin/buildings/add">
            <button className="px-4 py-2.5 text-sm font-bold rounded-lg flex items-center gap-1.5 bg-zinc-900 text-white hover:bg-red-600 transition-all border border-zinc-700 hover:border-red-600">
              <RiAddLine size={16} />
              {t('add_complex') || 'Add Complex'}
            </button>
          </Link>

          {/* Refresh */}
          <button
            onClick={fetchBuildings}
            className={`p-2.5 rounded-lg border transition-colors ${
              isDarkMode ? 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:text-white' : 'bg-white border-gray-200 text-zinc-400 hover:text-red-500'
            }`}
          >
            <RiRefreshLine size={16} />
          </button>
        </div>
      </div>
    </div>
  );

  const renderLoading = () => (
    loading && (
      <div className="flex flex-col items-center justify-center py-16">
        <div className={`inline-block h-10 w-10 animate-spin rounded-full border-4 ${isDarkMode ? 'border-red-500 border-t-transparent' : 'border-red-500 border-t-transparent'}`}></div>
        <p className={`mt-4 text-lg font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
          {t('loading_complexes') || 'Loading registry...'}
        </p>
      </div>
    )
  );

  const renderError = () => (
    error && !loading && (
      <div className={`p-6 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'} flex flex-col items-center justify-center text-center`}>
        <RiAlertLine className="text-4xl mb-4" />
        <h3 className="text-lg font-bold mb-2">{t('error_loading_complexes') || 'Error Loading Registry'}</h3>
        <p className="mb-4 font-medium">{error}</p>
        <button
          onClick={fetchBuildings}
          className={`px-5 py-2.5 font-semibold rounded-lg ${isDarkMode ? 'bg-red-700 text-white hover:bg-red-600' : 'bg-red-600 text-white hover:bg-red-700'} flex items-center transition-colors`}
        >
          <RiRefreshLine className="mr-2" />
          {t('try_again') || 'Try Again'}
        </button>
      </div>
    )
  );

  const renderActionError = () => (
    actionError && (
      <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400 border border-red-800' : 'bg-red-50 text-red-700 border border-red-200'} flex items-center justify-between`}>
        <div className="flex items-center font-medium">
          <RiAlertLine className="mr-2 text-xl flex-shrink-0" />
          <span>{actionError}</span>
        </div>
        <button onClick={() => setActionError(null)} className={`p-1 rounded-full ${isDarkMode ? 'hover:bg-red-900' : 'hover:bg-red-100'}`}>
          <RiCloseLine size={20} />
        </button>
      </div>
    )
  );

  const renderStatsHeader = () => {
    if (loading || error || buildings.length === 0) return null;

    const totalBuildings = buildings.length;
    const totalApartments = buildings.reduce((acc, b) => acc + (b.actual_apartments || b.totalApartments || 0), 0);
    const totalOccupied = buildings.reduce((acc, b) => acc + (b.occupied_apartments || 0), 0);
    const avgOccupancy = totalApartments > 0 ? (totalOccupied / totalApartments) * 100 : 0;

    const stats = [
      { label: t('total_complexes', 'TOTAL COMPLEXES'),      value: totalBuildings.toString().padStart(2, '0') },
      { label: t('total_showrooms_stat', 'TOTAL SHOWROOMS'), value: totalApartments.toString().padStart(2, '0') },
      { label: t('rented_sold_stat', 'RENTED / SOLD'),    value: totalOccupied.toString().padStart(2, '0') },
      { label: t('avg_utilization', 'AVG. UTILIZATION'),      value: `${avgOccupancy.toFixed(1)}%` },
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {stats.map((stat, idx) => (
          <div key={idx} className={`relative p-5 rounded-xl border overflow-hidden group ${
            isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
          }`}>
            <p className={`text-[10px] font-semibold uppercase tracking-widest mb-1 ${
              isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
            }`}>{stat.label}</p>
            <h3 className={`text-2xl font-bold tracking-tight ${
              isDarkMode ? 'text-white' : 'text-zinc-900'
            }`}>{stat.value}</h3>
            {/* Red underline that appears on hover */}
            <div className={`absolute bottom-0 left-0 h-0.5 w-full transition-all duration-300 ${
              isDarkMode ? 'bg-red-500/20 group-hover:bg-red-500' : 'bg-red-500/20 group-hover:bg-red-500'
            }`}/>
          </div>
        ))}
      </div>
    );
  };

  const renderEmptyState = () => (
    !loading && !error && filteredBuildings.length === 0 && (
      <div className="flex flex-col items-center justify-center py-16">
        <RiBuilding3Line className={`text-6xl mb-4 ${isDarkMode ? 'text-zinc-700' : 'text-zinc-300'}`} />
        <h3 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
          {searchTerm ? t('no_complexes_found') || 'No Complexes Found' : t('no_complexes_yet') || 'No Complexes Registered'}
        </h3>
        <p className={`text-sm mb-6 max-w-md text-center font-medium ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
          {searchTerm
            ? t('no_complexes_match_search', { search: searchTerm }) || `No locations match "${searchTerm}"`
            : t('no_complexes_message') || 'The logistics registry is empty. Add your first showroom park to get started.'
          }
        </p>
        {!searchTerm && (
          <Link
            to="/admin/buildings/add"
            className={`px-5 py-2.5 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-red-600 text-white hover:bg-red-700'} flex items-center font-bold transition-all shadow-md shadow-red-500/20`}
          >
            <RiAddLine className="mr-2" size={18} />
            {t('create_first_complex') || 'Register First Complex'}
          </Link>
        )}
      </div>
    )
  );

  const renderBuildingCard = (building, idx) => {
    const occupancyRate = calculateOccupancy(building);
    const totalApartments = building.actual_apartments || building.totalApartments || 0;
    const hasApartments = totalApartments > 0;

    return (
      <motion.div
        key={building.id}
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: idx * 0.05, duration: 0.35 }}
        className={`group flex flex-col rounded-xl border overflow-hidden hover:shadow-md transition-shadow ${
          isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-gray-200'
        }`}
      >
        {/* Top: ID + Name + Location + Blocks box */}
        <div className="p-5 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded uppercase tracking-wider mb-2 ${
              isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-gray-100 text-zinc-500'
            }`}>
              ID: {building.id?.toString().padStart(3, '0')}
            </span>
            <h3 className={`text-xl font-bold truncate group-hover:text-red-500 transition-colors ${
              isDarkMode ? 'text-white' : 'text-zinc-900'
            }`}>
              {building.name || t('unnamed_complex') || 'Unnamed Complex'}
            </h3>
            <div className={`flex items-center gap-1 text-xs mt-1 ${
              isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
            }`}>
              <RiMapPinLine size={12} />
              <span className="truncate">{building.address}</span>
            </div>
          </div>

          {/* Blocks box */}
          <div className={`text-center px-4 py-3 rounded-xl border shrink-0 ${
            isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-gray-200'
          }`}>
            <p className={`text-2xl font-bold leading-none ${
              isDarkMode ? 'text-white' : 'text-zinc-900'
            }`}>{building.totalFloors || 0}</p>
            <p className={`text-[9px] font-semibold uppercase tracking-widest mt-1 ${
              isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
            }`}>Blocks</p>
          </div>
        </div>

        {/* Stats: Active / Total */}
        <div className={`px-5 py-4 border-t border-b flex-grow ${
          isDarkMode ? 'border-zinc-800' : 'border-gray-100'
        }`}>
          {hasApartments ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className={`text-[10px] font-semibold uppercase tracking-widest mb-1 ${
                    isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                  }`}>{t('active') || 'Active'}</p>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-2xl font-bold ${
                      isDarkMode ? 'text-white' : 'text-zinc-900'
                    }`}>{building.occupied_apartments || 0}</span>
                    <span className={`text-[10px] font-semibold uppercase ${
                      isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                    }`}>Whs</span>
                  </div>
                </div>
                <div className={`border-s ps-4 ${
                  isDarkMode ? 'border-zinc-700' : 'border-gray-200'
                }`}>
                  <p className={`text-[10px] font-semibold uppercase tracking-widest mb-1 ${
                    isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                  }`}>{t('total') || 'Total'}</p>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-2xl font-bold ${
                      isDarkMode ? 'text-zinc-300' : 'text-zinc-600'
                    }`}>{totalApartments}</span>
                    <span className={`text-[10px] font-semibold uppercase ${
                      isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                    }`}>Cap</span>
                  </div>
                </div>
              </div>

              {/* Utilization bar */}
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <span className={`text-[10px] font-semibold uppercase tracking-widest ${
                    isDarkMode ? 'text-zinc-500' : 'text-zinc-400'
                  }`}>Utilization</span>
                  <span className={`text-sm font-bold ${
                    occupancyRate > 90 ? 'text-emerald-500'
                    : occupancyRate > 60 ? 'text-blue-500'
                    : 'text-red-500'
                  }`}>{occupancyRate.toFixed(1)}%</span>
                </div>
                <div className={`w-full h-1.5 rounded-full overflow-hidden ${
                  isDarkMode ? 'bg-zinc-800' : 'bg-gray-100'
                }`}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${occupancyRate}%` }}
                    transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                    className={`h-full rounded-full ${
                      occupancyRate > 90 ? 'bg-emerald-500' : occupancyRate > 60 ? 'bg-blue-500' : 'bg-red-500'
                    }`}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <p className={`text-xs font-semibold uppercase tracking-widest mb-3 ${
                isDarkMode ? 'text-zinc-600' : 'text-zinc-400'
              }`}>No Inventory Data</p>
              <button
                onClick={() => handleBulkCreate(building.id)}
                className={`text-xs font-semibold px-4 py-2 rounded-lg border transition-colors ${
                  isDarkMode ? 'bg-zinc-800 text-red-400 border-zinc-700 hover:bg-zinc-700' : 'bg-gray-50 text-red-600 border-gray-200 hover:bg-gray-100'
                }`}
              >
                {t('init_showrooms', 'Init Showrooms')}
              </button>
            </div>
          )}
        </div>

        {/* Action bar */}
        <div className={`px-5 py-3 flex items-center justify-between ${
          isDarkMode ? 'bg-zinc-950/40' : 'bg-gray-50'
        }`}>
          {/* Left actions: view, bulk-add, floor-plan */}
          <div className="flex items-center gap-1.5">
            <Link to={`/admin/buildings/${building.id}`}
              className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                isDarkMode ? 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500' : 'bg-white border-gray-200 text-zinc-400 hover:text-zinc-700 hover:border-gray-300'
              }`} title="View">
              <RiEyeLine size={15} />
            </Link>
            <button onClick={() => handleBulkCreate(building.id)}
              className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                isDarkMode ? 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white' : 'bg-white border-gray-200 text-zinc-400 hover:text-zinc-700'
              }`} title="Bulk Add">
              <RiFileAddLine size={15} />
            </button>
            <Link to={`/admin/buildings/${building.id}/floor-plan`}
              className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                isDarkMode ? 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white' : 'bg-white border-gray-200 text-zinc-400 hover:text-zinc-700'
              }`} title="Block Map">
              <RiBarChartBoxLine size={15} />
            </Link>
          </div>
          {/* Right: edit + delete */}
          <div className="flex items-center gap-1.5">
            <Link to={`/admin/buildings/${building.id}/edit`}
              className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                isDarkMode ? 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:text-white' : 'bg-white border-gray-200 text-zinc-400 hover:text-zinc-700'
              }`} title="Edit">
              <RiPencilLine size={15} />
            </Link>
            {(getCurrentUser()?.role === 'admin' || getCurrentUser()?.role === 'front_desk') && (
              <button onClick={() => openDeleteDialog(building.id, building.name)}
                className={`w-8 h-8 flex items-center justify-center rounded-lg border transition-colors ${
                  isDarkMode ? 'bg-zinc-900 border-zinc-700 text-zinc-500 hover:text-red-400' : 'bg-white border-gray-200 text-zinc-300 hover:text-red-500'
                }`} title="Delete">
                <RiDeleteBin6Line size={15} />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  const renderBuildingGrid = () => (
    !loading && !error && filteredBuildings.length > 0 && (
      <>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredBuildings.map((building, idx) => renderBuildingCard(building, idx))}
        </div>

        {buildings.length > 0 && filteredBuildings.length !== buildings.length && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className={`mt-10 py-4 px-6 rounded-2xl border flex items-center justify-center gap-2 ${isDarkMode ? 'bg-zinc-900/30 border-zinc-800 text-zinc-500' : 'bg-zinc-50 border-zinc-200 text-zinc-500'} text-xs font-bold uppercase tracking-widest`}
          >
            <RiHashtag className="text-red-500" />
            {t('showing_filtered_results', {
              showing: filteredBuildings.length,
              total: buildings.length
            }) || `Showing ${filteredBuildings.length} of ${buildings.length} locations`}
          </motion.div>
        )}
      </>
    )
  );

  const renderBulkCreatorModal = () => (
    showBulkCreator && bulkCreatorBuildingId && (
      <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
        <div className="max-w-6xl w-full max-h-[90vh] overflow-y-auto">
          <BulkApartmentCreator
            buildingId={bulkCreatorBuildingId}
            onSave={handleBulkCreateSave}
            onCancel={handleBulkCreateCancel}
          />
        </div>
      </div>
    )
  );

  return (
    <>
      {renderDeleteDialog()}

      <div className="space-y-4">
        {/* Header + Stats panel */}
        <div className={`p-6 rounded-xl border ${
          isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-gray-50 border-gray-200'
        }`}>
          {renderHeader()}
          {renderStatsHeader()}
        </div>

        {/* Grid */}
        <div>
          {renderLoading()}
          {renderError()}
          {renderActionError()}
          {renderEmptyState()}
          {renderBuildingGrid()}
        </div>
      </div>

      {showBuildingSelection && (
        <BuildingSelectionModal
          buildings={buildings}
          onSelect={handleBuildingSelect}
          onCancel={handleBuildingSelectionCancel}
        />
      )}

      {renderBulkCreatorModal()}
    </>
  );
};

export default BuildingList;